import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { opportunityQuestionsServices, opportunityServices } from '../../services'

import { showBanner } from '../../global-state/banner-slice'
import { RootState } from '../../store'
import { accessMeetsRequired } from '../../lib/permissions/adminFeatures'
import { OpportunityQuestionsPresenter } from './opportunity-questions-presenter'
import {
  AdminOpportunityQuestionListParams,
  type AdminOpportunityQuestion,
  type OpportunityQuestionStatus,
} from '../../services/opportunity-questions-service'

const statusOptions = [
  { text: 'All Status', value: '' },
  { text: 'Pending', value: 'pending' },
  { text: 'Answered', value: 'answered' },
]

const initialPagination = {
  total: 0,
  limit: 10,
  count: 0,
  offset: 0,
  page: 1,
}

const initialSearchParams = {
  page: 1,
  limit: 10,
  status: '',
  opportunity_id: '',
}

type OpportunityQuestionsSearchParams = {
  page: number
  limit: number
  status: OpportunityQuestionStatus | ''
  opportunity_id: string
}

export const OpportunityQuestionsContainer = () => {
  const dispatch = useDispatch()
  const { permissions } = useSelector((state: RootState) => state.gbcUser)
  const opportunitiesPermission = permissions?.opportunities ?? 'none'
  const canReply = accessMeetsRequired(opportunitiesPermission, 'write')
  const canView = accessMeetsRequired(opportunitiesPermission, 'read')

  const [questions, setQuestions] = useState<AdminOpportunityQuestion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState(initialPagination)
  const [opportunities, setOpportunities] = useState<{ text: string; value: string }[]>([
    { text: 'All opportunities', value: '' },
  ])
  const [searchParams, setSearchParams] = useState<OpportunityQuestionsSearchParams>(
    initialSearchParams as OpportunityQuestionsSearchParams,
  )

  const fetchOpportunityList = useCallback(async () => {
    try {
      setIsLoadingOpportunities(true)
      const response = await opportunityServices.getAllOpportunities()
      if (response.status === 200) {
        const list = response.opportunities || []
        setOpportunities([
          { text: 'All opportunities', value: '' },
          ...list.map((opportunity: { id: number; opportunity_title: string }) => ({
            text: `${opportunity.opportunity_title} (#${opportunity.id})`,
            value: String(opportunity.id),
          })),
        ])
        return
      }
      dispatch(showBanner({ text: 'Unable to load opportunities', variant: 'error-banner' }))
    } catch {
      dispatch(showBanner({ text: 'Unable to load opportunities', variant: 'error-banner' }))
    } finally {
      setIsLoadingOpportunities(false)
    }
  }, [dispatch])

  const fetchQuestions = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await opportunityQuestionsServices.listQuestions({
        page: currentPage,
        limit: pagination.limit,
        opportunity_id: searchParams.opportunity_id || undefined,
        status: searchParams.status || undefined,
      } as AdminOpportunityQuestionListParams)

      if (response && response.status === 200) {
        setQuestions(response.questions || [])
        setPagination(response.pagination)
      } else {
        dispatch(showBanner({ text: 'Unable to load opportunity FAQs', variant: 'error-banner' }))
        setQuestions([])
      }
    } catch {
      dispatch(showBanner({ text: 'Unable to load opportunity FAQs', variant: 'error-banner' }))
      setQuestions([])
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, searchParams, currentPage, pagination.limit])

  const handleSearchParams = ({ name, value }: { name: string; value: string }) => {
    setSearchParams((previousValues) => {
      const nextValues = { ...previousValues }
      if (name === 'status') {
        nextValues.status = value as OpportunityQuestionStatus | ''
      }
      if (name === 'opportunity_id') {
        nextValues.opportunity_id = value
      }
      nextValues.page = 1
      return nextValues
    })
    setCurrentPage(1)
    setPagination((previous) => ({ ...previous, page: 1, offset: 0 }))
  }

  const resetSearchParams = () => {
    setSearchParams(initialSearchParams as OpportunityQuestionsSearchParams)
    setCurrentPage(1)
    setPagination((previous) => ({ ...previous, page: 1, offset: 0 }))
  }

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page)
    setSearchParams((previousValues) => ({ ...previousValues, page }))
    setPagination((previous) => ({ ...previous, page, offset: (page - 1) * previous.limit }))
  }

  useEffect(() => {
    void fetchOpportunityList()
  }, [fetchOpportunityList])

  useEffect(() => {
    void fetchQuestions()
  }, [fetchQuestions])

  return (
    <OpportunityQuestionsPresenter
      opportunities={opportunities}
      isLoadingOpportunities={isLoadingOpportunities}
      questions={questions}
      isLoading={isLoading}
      searchParams={searchParams}
      statusList={statusOptions as { text: string; value: '' | 'pending' | 'answered' }[]}
      canReply={canReply}
      canViewReply={canView}
      pagination={pagination}
      currentPage={currentPage}
      handleSearchParams={handleSearchParams}
      resetSearchParams={resetSearchParams}
      handleCurrentPage={handleCurrentPage}
    />
  )
}
